import React from 'react';
import {
	Grid,
	TextField,
	FormControl,
	InputLabel,
	Select,
	MenuItem,
	Box,
	Typography,
	FormControlLabel,
	Switch,
} from '@mui/material';
import { ProductUnit, ProductInput } from '../../gql/graphql';

interface AttributesTabProps {
	productData: Partial<ProductInput>;
	updateProductData: (updates: Partial<ProductInput>) => void;
}

function AttributesTab({ productData, updateProductData }: AttributesTabProps) {
	const hasImage = (productData.images ?? []).some((url) => typeof url === 'string' && url.trim().length > 0);
	const handleChange = (field: keyof ProductInput) => (
		event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | any
	) => {
		const value = event.target.type === 'number'
			? parseFloat(event.target.value)
			: event.target.type === 'checkbox'
				? event.target.checked
				: event.target.value;
		updateProductData({ [field]: value });
	};

	return (
		<Box>
			<Typography variant="h6" gutterBottom>
				Product Attributes
			</Typography>
			<Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
				Define specific characteristics and availability settings
			</Typography>

			<Grid container spacing={3}>
				{/* Unit */}
				<Grid item xs={12} md={6}>
					<FormControl fullWidth>
						<InputLabel>Unit of Measurement</InputLabel>
						<Select
							label="Unit of Measurement"
							name="unit"
							value={productData.unit || ProductUnit.Piece}
							onChange={handleChange('unit')}
						>
							<MenuItem value={ProductUnit.Piece}>Piece</MenuItem>
							<MenuItem value={ProductUnit.Kg}>Kilogram (KG)</MenuItem>
							<MenuItem value={ProductUnit.Gram}>Gram (g)</MenuItem>
							<MenuItem value={ProductUnit.Liter}>Liter (L)</MenuItem>
							<MenuItem value={ProductUnit.Ml}>Milliliter (mL)</MenuItem>
						</Select>
					</FormControl>
				</Grid>

				{/* Preparation Time */}
				<Grid item xs={12} md={6}>
					<TextField
						fullWidth
						label="Preparation Time"
						name="preparationTime"
						type="number"
						value={productData.preparationTime || ''}
						onChange={handleChange('preparationTime')}
						helperText="Time in minutes to prepare/deliver"
					/>
				</Grid>

				<Grid item xs={12}>
					<Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
						Perishability Settings
					</Typography>
				</Grid>

				{/* Is Perishable */}
				<Grid item xs={12} md={6}>
					<FormControlLabel
						control={
							<Switch
								checked={productData.isPerishable || false}
								onChange={handleChange('isPerishable')}
							/>
						}
						label="Perishable Product"
					/>
					<Typography variant="caption" display="block" color="text.secondary">
						Enable if product has an expiration date
					</Typography>
				</Grid>

				{/* Shelf Life */}
				{productData.isPerishable && (
					<Grid item xs={12} md={6}>
						<TextField
							fullWidth
							label="Shelf Life (Days)"
							name="shelfLifeDays"
							type="number"
							value={productData.shelfLifeDays || ''}
							onChange={handleChange('shelfLifeDays')}
							helperText="Number of days product remains fresh"
						/>
					</Grid>
				)}

				<Grid item xs={12}>
					<Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
						Availability Settings
					</Typography>
				</Grid>

				{/* Is Active */}
				<Grid item xs={12} md={6}>
					<FormControlLabel
						control={
							<Switch
								checked={productData.isActive !== false}
								onChange={handleChange('isActive')}
							/>
						}
						label="Active Product"
					/>
					<Typography variant="caption" display="block" color="text.secondary">
						Inactive products won't appear in lists or be available for sale
					</Typography>
				</Grid>

				{/* Is Available Online */}
				<Grid item xs={12} md={6}>
					<FormControlLabel
						control={
							<Switch
								// Explicitly `=== true`: this is off unless it has been
								// deliberately turned on. `!== false` would have shown new
								// products as already online.
								checked={productData.isAvailableOnline === true}
								onChange={handleChange('isAvailableOnline')}
								// The delivery app only lists products with a photo, and the
								// API refuses to put one online without it — so the switch
								// says so here rather than failing on save. Only the way ON
								// is blocked: a product that is online without a photo (from
								// before the rule) must still be switchable OFF.
								disabled={!hasImage && productData.isAvailableOnline !== true}
							/>
						}
						label="Sell in the delivery app"
					/>
					<Typography variant="caption" display="block" color={hasImage ? 'text.secondary' : 'warning.main'}>
						{hasImage
							? 'Off by default. Turning this on publishes the product to customers in the delivery app — check the price, unit and stock first.'
							: 'Add at least one image URL on the Basic info tab first. The delivery app only shows products with a photo.'}
					</Typography>
				</Grid>
			</Grid>
		</Box>
	);
}

export default AttributesTab;
